import 'reflect-metadata';
import type { ISubscriptionUserRepository } from './subscriptionUserRepository/ISubscriptionUserRepository';
import type ITenantConfigRepository from './tenantConfigRepository/ITenantConfigRepository';
import type ITenantMemberRepository from './tenantMemberRepository/ITenantMemberRepository';
import type ITenantSsoProviderRepository from './tenantSsoProviderRepository/ITenantSsoProviderRepository';
import { Container } from 'inversify';
import { SaasOverviewRepository } from './SaasOverviewRepository/impl/SaasOverviewRepository';
import { SubscriptionCountStatRepository } from './SubscriptionCountStatRepository/impl/SubscriptionCountStatRepository';
import { ApprovalRepository } from './approvalRepository/impl/ApprovalRepository';
import { BillingOptimizationRepository } from './billingOptimizationRepository/impl/BillingOptimizationRepository';
import { CommonCodeRepository } from './commonCodeRepository/impl/CommonCodeRepository';
import { CompanyRepository } from './companyRepository/impl/CompanyRepository';
import { ExchangeRateRepository } from './exchangeRateRepository/impl/ExchangeRateRepository';
import { FileRepository } from './fileRepository/impl/FileRepository';
import { GoogleOauthRepository } from './googleOauthRepository/impl/GoogleOauthRepository';
import { InquiryRepository } from './inquiryRepository/impl/InquiryRepository';
import { LicenseRepository } from './licenseRepository/impl/LicenseRepository';
import { MfaRepository } from './mfaRepository/impl/MfaRepository';
import { MonthlyBillingRepository } from './monthlyBillingRepository/impl/MonthlyBillingRepository';
import { OauthRepository } from './oauthRepository/impl/OauthRepository';
import { PrivacyPolicyRepository } from './privacyPolicyRepository/impl/PrivacyPolicyRepository';
import { ProductConnectorRepository } from './productConnectorRepository/impl/ProductConnectorRepository';
import { ProductRequestRepository } from './productRequestRepository/impl/ProductRequestRepository';
import { ProductSubscriptionRepository } from './productSubscriptionRepository/impl/ProductSubscriptionRepository';
import { ProductRelationsRepository } from './productsRelationsRepository/impl/ProductRelationsRepository';
import { ScimTokenRepository } from './scimTokenRepository/impl/ScimTokenRepository';
import { TenantSsoProviderRepository } from './tenantSsoProviderRepository/impl/TenantSsoProviderRepository';
import { SoftwareCategoryRepository } from './softwareCategoryRepository/impl/SoftwareCategoryRepository';
import { SoftwareRepository } from './softwareRepository/impl/SoftwareRepository';
import { SubscriptionBillilngStatRepository } from './subscriptionBillingStatRepository/impl/SubscriptionBillingStatRepository';
import { SubscriptionConnectionRepository } from './subscriptionConnectionRepository/impl/SubscriptionConnectionRepository';
import { SubscriptionRepository } from './subscriptionRepository/impl/SubscriptionRepository';
import { SubscriptionRequestRepository } from './subscriptionRequestRepository/impl/SubscriptionRequestRepository';
import { SubscriptionUseRequestRepository } from './subscriptionUseRequestRepository/impl/SubscriptionUseRequestRepository';
import { SubscriptionUserRepository } from './subscriptionUserRepository/impl/SubscriptionUserRepository';
import { TechnicalSupportRepository } from './technicalSupportRepository/impl/TechnicalSupportRepository';
import { TenantBillingStatRepository } from './tenantBillingStatRepository/impl/TenantBillingStatRepository';
import { TenantConfigRepository } from './tenantConfigRepository/impl/TenantConfigRepository';
import { TenantMemberRepository } from './tenantMemberRepository/impl/TenantMemberRepository';
import { TenantMonthlyBillingRepository } from './tenantMonthlyBillingRepository/impl/TenantMonthlyBillingRepository';
import { TenantRepository } from './tenantRepository/impl/TenantRepository';
import { TenantRequestRepository } from './tenantRequestRepository/impl/TenantRequestRepository';
import { DailyUsageRepository } from './usage/dailyUsageRepository/impl/DailyUsageRepository';
import { HourlyUsageRepository } from './usage/hourlyUsageRepository/impl/HourlyUsageRepository';
import { MonthlyUsageRepository } from './usage/monthlyUsageRepository/impl/MonthlyUsageRepository';
import { RealtimeUsageRepository } from './usage/realtimeUsageRepository/impl/RealtimeUsageRepository';
import { UserGroupRepository } from './userGroupsRepository/impl/UserGroupRepository';
import { UserMonthlyBillingRepository } from './userMonthlyBillingRepository/impl/UserMonthlyBillingRepository';
import { UserRepository } from './userRepository/impl/UserRepository';
import { UserStatRepository } from './userStatRepository/impl/UserStatRepository';
import { WorkflowBaseTriggerRepository } from './workflowBaseTriggerRepository/impl/WorkflowBaseTriggerRepository';
import { WorkflowRepository } from './workflowRepository/impl/WorkflowRepository';

export const KEYS = {
  BILLING_OPTIMIZATION_REPOSITORY: Symbol('BillingOptimizationRepository'),
  TENANT_REPOSITORY: Symbol('TenantRepository'),
  TENANT_REQUEST_REPOSITORY: Symbol('TenantRequestRepository'),
  SUBSCRIPTION_REPOSITORY: Symbol('SubscriptionRepository'),
  SUBSCRIPTION_REQUEST_REPOSITORY: Symbol('SubscriptionRequestRepository'),
  SOFTWARE_REPOSITORY: Symbol('SoftwareRepository'),
  USER_REPOSITORY: Symbol('UserRepository'),
  TENANT_MONTHLY_BILLING_REPOSITORY: Symbol('TenantMonthlyBillingRepository'),
  DAILY_USAGE_REPOSITORY: Symbol('DailyUsageRepository'),
  HOURLY_USAGE_REPOSITORY: Symbol('HourlyUsageRepository'),
  REALTIME_USAGE_REPOSITORY: Symbol('RealtimeUsageRepository'),
  MONTHLY_USAGE_REPOSITORY: Symbol('MonthlyUsageRepository'),
  USERGROUP_REPOSITORY: Symbol('UserGroupRepository'),
  USER_MONTHLY_BILLING_REPOSITORY: Symbol('UserMonthlyBillingRepository'),
  SOFTWARE_CATEGORY_REPOSITORY: Symbol('SoftwareCategoryRepository'),
  USER_SUBSCRIPTION_STAT_REPOSITORY_DEPRECATED: Symbol('UserSubscriptionStatRepository'),
  PRODUCT_RELATIONS_REPOSITORY: Symbol('ProductRelationsRepository'),
  PRODUCT_REQUEST_REPOSITORY: Symbol('ProductRequestRepository'),
  PRODUCT_SUBSCRIPTION_REPOSITORY: Symbol('ProductSubscriptionRepository'),
  COMMON_CODE_REPOSITORY: Symbol('CommonCodeRepository'),
  COMPANY_REPOSITORY: Symbol('CompanyRepository'),
  SUBSCRIPTION_USE_REQUEST: Symbol('SubscriptionUseRequestRepository'),
  MONTHLY_BILLING_REPOSITORY: Symbol('MonthlyBillingRepository'),
  LICENSE_REPOSITORY: Symbol('LicenseRepository'),
  REALTIME_BILLING_REPOSITORY: Symbol('RealtimeBillingRepository'),
  SUBSCRIPTION_CONNECTION_REPOSITORY: Symbol('SubscriptionConnectionRepository'),
  PRODUCT_CONNECTOR_REPOSITORY: Symbol('ProductConnectorRepository'),
  INQUIRY_REPOSITORY: Symbol('InquiryRepository'),
  WORKFLOW_REPOSITORY: Symbol('WorkflowRepository'),
  WORKFLOW_BASE_TRIGGER_REPOSITORY: Symbol('WorkflowBaseTriggerRepository'),
  OAUTH_REPOSITORY: Symbol('OauthRepository'),
  GOOGLE_OAUTH_REPOSITORY: Symbol('GoogleOauthRepository'),
  TENENT_BILLIG_STAT_REPOSITORY: Symbol('TenantBillingStatRepository'),
  TENANT_MEMBER_REPOSITORY: Symbol('TenantMemberRepository'),
  EXCHANGE_RATE_REPOSITORY: Symbol('ExchangeRateRepository'),
  FILE_REPOSITORY: Symbol('FileRepository'),
  USER_STAT_REPOSITORY: Symbol('UserStatRepository'),
  SUBSCRIPTION_USER_REPOSITORY: Symbol('SubscriptionUserRepository'),
  SUBSCRIPTION_BILLING_STAT_REPOSITORY: Symbol('SubscriptionBillingStatRepositry'),
  TECHNICAL_SUPPORT_REPOSITORY: Symbol('TechnicalSupporyRepository'),
  APPROVAL_REPOSITORY: Symbol('ApprovalRepository'),
  TENANT_CONFIG_REPOSITORY: Symbol('TenantConfigRepository'),
  MFA_REPOSITORY: Symbol('MfaRepository'),
  PRIVACY_POLICY_REPOSITORY: Symbol('PrivacyPolicyRepository'),
  SCIM_TOKEN_REPOSITORY: Symbol('ScimTokenRepository'),
  TENANT_SSO_PROVIDER_REPOSITORY: Symbol('TenantSsoProviderRepository'),
  SAAS_OVERVIEW_REPOSITORY: Symbol('SaasOverviewRepository'),
};

const _container = new Container();

_container.bind(KEYS.BILLING_OPTIMIZATION_REPOSITORY).to(BillingOptimizationRepository).inSingletonScope();
_container.bind(KEYS.TENANT_REPOSITORY).to(TenantRepository).inSingletonScope();
_container.bind(KEYS.TENANT_REQUEST_REPOSITORY).to(TenantRequestRepository).inSingletonScope();
_container.bind(KEYS.SUBSCRIPTION_REPOSITORY).to(SubscriptionRepository).inSingletonScope();
_container.bind(KEYS.SUBSCRIPTION_REQUEST_REPOSITORY).to(SubscriptionRequestRepository).inSingletonScope();
_container.bind(KEYS.SOFTWARE_REPOSITORY).to(SoftwareRepository).inSingletonScope();
_container.bind(KEYS.USER_REPOSITORY).to(UserRepository).inSingletonScope();
_container.bind(KEYS.TENANT_MONTHLY_BILLING_REPOSITORY).to(TenantMonthlyBillingRepository).inSingletonScope();
_container.bind(KEYS.DAILY_USAGE_REPOSITORY).to(DailyUsageRepository).inSingletonScope();
_container.bind(KEYS.HOURLY_USAGE_REPOSITORY).to(HourlyUsageRepository).inSingletonScope();
_container.bind(KEYS.REALTIME_USAGE_REPOSITORY).to(RealtimeUsageRepository).inSingletonScope();
_container.bind(KEYS.MONTHLY_USAGE_REPOSITORY).to(MonthlyUsageRepository).inSingletonScope();
_container.bind(KEYS.USERGROUP_REPOSITORY).to(UserGroupRepository).inSingletonScope();
_container.bind(KEYS.USER_MONTHLY_BILLING_REPOSITORY).to(UserMonthlyBillingRepository).inSingletonScope();
_container.bind(KEYS.SOFTWARE_CATEGORY_REPOSITORY).to(SoftwareCategoryRepository).inSingletonScope();
_container
  .bind(KEYS.USER_SUBSCRIPTION_STAT_REPOSITORY_DEPRECATED)
  .to(SubscriptionCountStatRepository)
  .inSingletonScope();
_container.bind(KEYS.PRODUCT_RELATIONS_REPOSITORY).to(ProductRelationsRepository).inSingletonScope();
_container.bind(KEYS.PRODUCT_REQUEST_REPOSITORY).to(ProductRequestRepository).inSingletonScope();
_container.bind(KEYS.PRODUCT_SUBSCRIPTION_REPOSITORY).to(ProductSubscriptionRepository).inSingletonScope();
_container.bind(KEYS.COMMON_CODE_REPOSITORY).to(CommonCodeRepository).inSingletonScope();
_container.bind(KEYS.COMPANY_REPOSITORY).to(CompanyRepository).inSingletonScope();
_container.bind(KEYS.SUBSCRIPTION_USE_REQUEST).to(SubscriptionUseRequestRepository).inSingletonScope();
_container.bind(KEYS.MONTHLY_BILLING_REPOSITORY).to(MonthlyBillingRepository).inSingletonScope();
_container.bind(KEYS.LICENSE_REPOSITORY).to(LicenseRepository).inSingletonScope();
_container.bind(KEYS.SUBSCRIPTION_CONNECTION_REPOSITORY).to(SubscriptionConnectionRepository).inSingletonScope();
_container.bind(KEYS.PRODUCT_CONNECTOR_REPOSITORY).to(ProductConnectorRepository).inSingletonScope();
_container.bind(KEYS.INQUIRY_REPOSITORY).to(InquiryRepository).inSingletonScope();
_container.bind(KEYS.WORKFLOW_REPOSITORY).to(WorkflowRepository).inSingletonScope();
_container.bind(KEYS.WORKFLOW_BASE_TRIGGER_REPOSITORY).to(WorkflowBaseTriggerRepository).inSingletonScope();
_container.bind(KEYS.OAUTH_REPOSITORY).to(OauthRepository).inSingletonScope();
_container.bind(KEYS.GOOGLE_OAUTH_REPOSITORY).to(GoogleOauthRepository).inSingletonScope();
_container.bind(KEYS.TENENT_BILLIG_STAT_REPOSITORY).to(TenantBillingStatRepository).inSingletonScope();
_container.bind(KEYS.TENANT_MEMBER_REPOSITORY).to(TenantMemberRepository).inSingletonScope();
_container.bind(KEYS.EXCHANGE_RATE_REPOSITORY).to(ExchangeRateRepository).inSingletonScope();
_container.bind(KEYS.FILE_REPOSITORY).to(FileRepository).inSingletonScope();
_container.bind(KEYS.USER_STAT_REPOSITORY).to(UserStatRepository).inSingletonScope();
_container.bind(KEYS.SUBSCRIPTION_USER_REPOSITORY).to(SubscriptionUserRepository).inSingletonScope();
_container.bind(KEYS.SUBSCRIPTION_BILLING_STAT_REPOSITORY).to(SubscriptionBillilngStatRepository).inSingletonScope();
_container.bind(KEYS.TECHNICAL_SUPPORT_REPOSITORY).to(TechnicalSupportRepository).inSingletonScope();
_container.bind(KEYS.APPROVAL_REPOSITORY).to(ApprovalRepository).inSingletonScope();
_container.bind(KEYS.TENANT_CONFIG_REPOSITORY).to(TenantConfigRepository).inSingletonScope();
_container.bind(KEYS.MFA_REPOSITORY).to(MfaRepository).inSingletonScope();
_container.bind(KEYS.PRIVACY_POLICY_REPOSITORY).to(PrivacyPolicyRepository).inSingletonScope();
_container.bind(KEYS.SCIM_TOKEN_REPOSITORY).to(ScimTokenRepository).inSingletonScope();
_container.bind(KEYS.TENANT_SSO_PROVIDER_REPOSITORY).to(TenantSsoProviderRepository).inSingletonScope();
_container.bind(KEYS.SAAS_OVERVIEW_REPOSITORY).to(SaasOverviewRepository).inSingletonScope();
export const repository = _container;

export const tenantMemberRepository = _container.get<ITenantMemberRepository>(KEYS.TENANT_MEMBER_REPOSITORY);
export const subscriptionUserRepository = _container.get<ISubscriptionUserRepository>(
  KEYS.SUBSCRIPTION_USER_REPOSITORY,
);
export const tenantConfigRepository = _container.get<ITenantConfigRepository>(KEYS.TENANT_CONFIG_REPOSITORY);
export const tenantSsoProviderRepository = _container.get<ITenantSsoProviderRepository>(
  KEYS.TENANT_SSO_PROVIDER_REPOSITORY,
);
